import { DeliveryMethodModel } from '@/http/models/DeliveryMethod'
import { toast } from 'sonner'

import { useState } from 'react'

import { DeliveryMethodModel as DeliveryMethodType } from '@/types'

import { useFetchDeliveryMethods } from './useFetchDeliveryMethods'

type DialogMode = 'create' | 'edit' | 'view'

export const useDeliveryMethodsTable = () => {
  // Dialog state
  const [mode, setMode] = useState<DialogMode>('create')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editMethod, setEditMethod] = useState<DeliveryMethodType.DeliveryMethod | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [methodToDelete, setMethodToDelete] = useState<DeliveryMethodType.DeliveryMethod | null>(
    null
  )

  // Loading states
  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch delivery methods data
  const { data, loading, pagination, setPage, setPageSize, refresh } = useFetchDeliveryMethods()

  // Dialog handlers
  const openCreateDialog = () => {
    setMode('create')
    setEditMethod(null)
    setDialogOpen(true)
  }

  const openViewDialog = (method: DeliveryMethodType.DeliveryMethod) => {
    setMode('view')
    setEditMethod(method)
    setDialogOpen(true)
  }

  const openEditDialog = (method: DeliveryMethodType.DeliveryMethod) => {
    setMode('edit')
    setEditMethod(method)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditMethod(null)
  }

  // Delete handlers
  const handleDeleteClick = (method: DeliveryMethodType.DeliveryMethod) => {
    setMethodToDelete(method)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!methodToDelete?._id) return

    setDeleteLoading(true)
    const response = await DeliveryMethodModel.delete({
      params: { id: methodToDelete._id },
    })

    if (response.success) {
      toast.success('Delivery method deleted successfully')
      setDeleteDialogOpen(false)
      setMethodToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete delivery method')
    }
    setDeleteLoading(false)
  }

  // Status toggle handler
  const toggleStatus = async (method: DeliveryMethodType.DeliveryMethod) => {
    const newStatus = method.status === 'active' ? 'inactive' : 'active'

    const response = await DeliveryMethodModel.update({
      params: { key: method.key },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Method status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  // CRUD handlers
  const handleCreateMethod = async (data: any) => {
    setCreateLoading(true)
    const response = await DeliveryMethodModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Delivery method created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create delivery method')
    }
    setCreateLoading(false)
  }

  const handleUpdateMethod = async (key: string, data: any) => {
    setUpdateLoading(true)
    const response = await DeliveryMethodModel.update({
      params: { key },
      body: data,
    })

    if (response.success) {
      toast.success('Delivery method updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update delivery method')
    }
    setUpdateLoading(false)
  }

  // Computed values
  const stats = {
    totalMethods: pagination.totalCount,
    activeMethods: (data as DeliveryMethodType.DeliveryMethod[]).filter(
      (m) => m.status === 'active'
    ).length,
    inactiveMethods: (data as DeliveryMethodType.DeliveryMethod[]).filter(
      (m) => m.status === 'inactive'
    ).length,
  }

  return {
    // Data
    loading,
    data: data as DeliveryMethodType.DeliveryMethod[],
    pagination,
    stats,

    // Dialog state
    mode,
    dialogOpen,
    editMethod,

    // Delete dialog state
    deleteDialogOpen,
    methodToDelete,

    // Loading states
    createLoading,
    updateLoading,
    deleteLoading,

    // Pagination handlers
    setPage,
    setPageSize,
    refresh,

    // Dialog handlers
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,

    // Delete handlers
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,

    // CRUD handlers
    handleCreateMethod,
    handleUpdateMethod,
    toggleStatus,
  }
}
